import { useMutation, useQuery } from "@tanstack/react-query";
import { X, Info } from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router";
import { toast } from "@hubertai/hubert-component-library";
import Illustration from "../../assets/images/illustration-4.png";
import { cancelOutreachVoiceConversation, fetchOutreachCandidateById } from "../../behavior/api/outreach";
import { Button, Heading, Text } from "../../components/elements";
import TransitionPage from "../transition/TransitionPage";
import { PageRoute } from "../../constants";
import "./OutreachCandidatePage.css";
import OutreachHeader from "./components/OutreachHeader";
import OutreachFooter from "./components/OutreachFooter";

const OutreachCandidatePage = () => {
    const { candidateId } = useParams<{ candidateId: string }>();
    const navigate = useNavigate();
    const { t } = useTranslation();
    const [isRadioSelected, setIsRadioSelected] = useState(false);

    const {
        data: candidate,
        isLoading,
        error,
    } = useQuery({
        queryKey: ["outreachCandidate", candidateId],
        queryFn: fetchOutreachCandidateById(candidateId || ""),
        enabled: !!candidateId,
    });

    const cancelMutation = useMutation({
        mutationFn: async (_: { isDecline: boolean }) => {
            if (!candidateId) throw new Error("No candidate ID");
            return cancelOutreachVoiceConversation(candidateId);
        },
        onSuccess: (_data, variables) => {
            if (variables.isDecline) {
                toast.success(t("Outreach.Toast_DeclineSuccess"));
            } else {
                toast.info(t("Outreach.Toast_CancelVoiceInfo"));
            }
        },
        onError: (error) => {
            console.error("Error cancelling interview:", error);
            toast.error(t("Outreach.Toast_CancelError"));
        },
    });

    const handleStartChat = () => {
        if (!candidateId) return;
        navigate(PageRoute.OUTREACH_CANDIDATE_CHAT.replace(":candidateId", candidateId));
    };

    const handleConfirmAndDecline = () => {
        cancelMutation.mutate({ isDecline: true });
    };

    if (isLoading) {
        return (
            <TransitionPage
                title={t("TransitionPage.loading-title")}
                subtitle={t("Outreach.FetchingCandidateDetails")}
            />
        );
    }

    if (error || !candidate) {
        return (
            <div className="outreach-candidate-page">
                <div className="outreach-candidate-container">
                    <div className="outreach-main-content">
                        <Heading level="h3">{t("Outreach.CandidateNotFound")}</Heading>
                        <Text>{t("Outreach.CandidateNotFoundDesc")}</Text>
                    </div>
                </div>
            </div>
        );
    }

    const companyName = candidate.campaign.companyName || candidate.campaign.customerName;

    if (!candidate.actions.canChatInterview && !candidate.actions.canCancel) {
        return (
            <div className="outreach-candidate-page">
                <div className="outreach-candidate-container">
                    <OutreachHeader companyName={companyName} status="connected" />
                    <div className="outreach-main-content">
                        <img src={Illustration} alt={t("Outreach.InterviewEnded")} className="status-illustration" />
                        <Heading level="h2">{t("Outreach.InterviewEnded")}</Heading>
                        <Text className="description">{t("Outreach.InterviewEndedDesc1")}</Text>
                        <Text className="description">{t("Outreach.InterviewEndedDesc2")}</Text>
                    </div>
                    <OutreachFooter companyName={companyName} privacyPolicies={candidate.legal.privacyPolicies} />
                </div>
            </div>
        );
    }

    return (
        <div className="outreach-candidate-page">
            <div className="outreach-candidate-container">
                <OutreachHeader companyName={companyName} status="connected" />

                <div className="outreach-main-content">
                    <img src={Illustration} alt="Illustration" className="status-illustration" />

                    <Heading level="h2">
                        {t("Outreach.ReadyTitle", { firstName: candidate.candidate.firstName })}
                    </Heading>

                    <Text className="description">
                        {t("Outreach.InvitationDesc", {
                            companyName: companyName,
                        })}
                    </Text>

                    {candidate.actions.canChatInterview && (
                        <div className="actions-section">
                            <Button fullWidth size="large" onClick={handleStartChat}>
                                {t("Outreach.Button_StartChat")}
                            </Button>

                            <div className="cancel-notice">
                                <Info size={14} />
                                <Text>{t("Outreach.CancelNotice")}</Text>
                            </div>
                        </div>
                    )}

                    {candidate.actions.canCancel && (
                        <div className="decline-section">
                            <div className="or-section">
                                <div className="or-seperator-line" />
                                <div className="or-text">{t("Outreach.PreferNotToContinue")}</div>
                            </div>

                            <div className="radio-option" onClick={() => setIsRadioSelected(!isRadioSelected)}>
                                <input
                                    type="radio"
                                    checked={isRadioSelected}
                                    readOnly
                                    id="decline-radio"
                                    style={{ accentColor: "#664de5", pointerEvents: "none" }}
                                />
                                <label htmlFor="decline-radio" style={{ pointerEvents: "none" }}>
                                    {t("Outreach.DeclineLabel")}
                                </label>
                            </div>

                            <Button
                                fullWidth
                                size="large"
                                secondary
                                style={{
                                    backgroundColor: isRadioSelected ? "#ff5a5f" : "#ffc9c9",
                                    color: "#fff",
                                    borderColor: "transparent",
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    gap: "0.5rem",
                                    opacity: 1,
                                    cursor: isRadioSelected ? "pointer" : "not-allowed",
                                }}
                                disabled={!isRadioSelected}
                                onClick={handleConfirmAndDecline}
                            >
                                <X size={18} />
                                {cancelMutation.isPending
                                    ? t("Outreach.Processing")
                                    : t("Outreach.Button_ConfirmAndDecline")}
                            </Button>
                        </div>
                    )}
                </div>

                <OutreachFooter companyName={companyName} privacyPolicies={candidate.legal.privacyPolicies} />
            </div>
        </div>
    );
};

export default OutreachCandidatePage;
